import { computed, createModel } from '@preact/signals-react';
import type { Model, ModelConstructor, ReadonlySignal } from '@preact/signals-react';
import type { Logger } from '@thinkalpha/ui-ioc/core/types/Logger';
import type { ColumnTemplate } from '@thinkalpha/ui-types/contracts/column-template';
import { AGGREGATED_RISK_WIDGET_CONFIG_BY_KIND, columnTemplateStateSchema } from 'src/features/aggregated-risk/config';
import { AggregatedRiskSelfOwnedModel } from 'src/models/AggregatedRiskWidgetModel/AggregatedRiskSelfOwnedModel';
import type { AggregatedRiskSelfOwnedEventBus } from 'src/models/AggregatedRiskWidgetModel/AggregatedRiskSelfOwnedModel';
import { InstrumentSelectionModel, instrumentSelectionStateSchema } from 'src/models/InstrumentSelectionModel';
import type { InstrumentSelectionModelEventBus } from 'src/models/InstrumentSelectionModel';
import { stickyFilterStateSchema } from 'src/models/StickyFilterModel';
import type { TableModel } from 'src/models/TableModel';
import type { AggregatedRiskTableFilters } from 'src/queries/aggregatedRiskTables';
import { EMPTY_AGGREGATED_RISK_TABLE_FILTERS } from 'src/queries/aggregatedRiskTables';
import type { WidgetHostContainer } from 'src/services/WorkspaceService/Container/Container';
import type { WidgetId } from 'src/services/WorkspaceService/selfOwnedWidget';
import type { ReactBindings } from 'src/types/bindings';
import { MergedEventBus } from 'src/types/EventBus/EventBus';
import type { EventBus, EventBusEvents, EventBusSignals } from 'src/types/EventBus/EventBus';
import type { z } from 'zod';

export const symbolsWidgetStateSchema = stickyFilterStateSchema.merge(instrumentSelectionStateSchema).extend({
    columnTemplate: columnTemplateStateSchema,
});

export type SymbolsWidgetState = z.infer<typeof symbolsWidgetStateSchema>;

export type SymbolsSelfOwnedEventBus = EventBus<
    EventBusSignals<AggregatedRiskSelfOwnedEventBus> & EventBusSignals<InstrumentSelectionModelEventBus>,
    EventBusEvents<AggregatedRiskSelfOwnedEventBus> & EventBusEvents<InstrumentSelectionModelEventBus>
>;

interface SymbolsSelfOwnedModelInit {
    container: WidgetHostContainer;
    tabId: WidgetId;
    persisted: SymbolsWidgetState;
    logger: Logger;
    queryClient: ReactBindings['QueryClient'];
    slugMapServiceProvider: ReactBindings['SlugMapServiceProvider'];
    table: TableModel;
}

interface SymbolsSelfOwnedModelShape {
    readonly dirty: ReadonlySignal<boolean>;
    readonly eventBus: SymbolsSelfOwnedEventBus;
    readonly tableFilters: ReadonlySignal<AggregatedRiskTableFilters>;
    readonly instrument: ReadonlySignal<string | null>;
    readonly columnTemplate: ReadonlySignal<ColumnTemplate | null>;
    readonly groupByIndicatorId: ReadonlySignal<string | null>;
    readonly columnTemplateError: ReadonlySignal<Error | null>;
    toJSON(): SymbolsWidgetState;
    markClean(): void;
}

export const SymbolsSelfOwnedModel: ModelConstructor<SymbolsSelfOwnedModelShape, [SymbolsSelfOwnedModelInit]> =
    createModel<SymbolsSelfOwnedModelShape, [SymbolsSelfOwnedModelInit]>((init: SymbolsSelfOwnedModelInit) => {
        const aggregatedRiskModel = new AggregatedRiskSelfOwnedModel({
            ...init,
            config: AGGREGATED_RISK_WIDGET_CONFIG_BY_KIND.symbols,
        });
        const instrumentSelectionModel = new InstrumentSelectionModel({
            persisted: init.persisted,
            allowedInstrumentTypes: ['equity'],
        });
        const dirty = computed(() => aggregatedRiskModel.dirty.value || instrumentSelectionModel.dirty.value);
        const eventBus: SymbolsSelfOwnedEventBus = MergedEventBus.from<AggregatedRiskSelfOwnedEventBus>(
            aggregatedRiskModel.eventBus,
        ).merge<InstrumentSelectionModelEventBus>(instrumentSelectionModel.eventBus);
        const tableFilters = computed<AggregatedRiskTableFilters>(() => ({
            ...EMPTY_AGGREGATED_RISK_TABLE_FILTERS,
            instrument: instrumentSelectionModel.instrument.value,
        }));

        return {
            dirty,
            eventBus,
            tableFilters,
            instrument: instrumentSelectionModel.instrument,
            columnTemplate: aggregatedRiskModel.columnTemplate,
            groupByIndicatorId: aggregatedRiskModel.groupByIndicatorId,
            columnTemplateError: aggregatedRiskModel.columnTemplateError,
            toJSON(): SymbolsWidgetState {
                return {
                    ...aggregatedRiskModel.toJSON(),
                    ...instrumentSelectionModel.toJSON(),
                };
            },
            markClean(): void {
                aggregatedRiskModel.markClean();
                instrumentSelectionModel.markClean();
            },
        };
    });

export type SymbolsSelfOwnedModel = Model<SymbolsSelfOwnedModelShape>;
